import { Check, Circle, Loader2, Search } from "lucide-react";
import type { ChainOfThoughtStep } from "../types";
import { MessageResponse } from "@/components/ai-elements/message";

interface ChainOfThoughtBlockProps {
  steps: ChainOfThoughtStep[];
  isStreaming?: boolean;
}

const STATUS_ICONS: Record<string, React.ElementType> = {
  complete: Check,
  active: Loader2,
  pending: Circle,
};

const STATUS_COLORS: Record<string, string> = {
  complete: "text-green-500",
  active: "text-blue-500",
  pending: "text-muted-foreground/50",
};

export function ChainOfThoughtBlock({ steps, isStreaming }: ChainOfThoughtBlockProps) {
  return (
    <div className="my-2 rounded-lg border border-border p-3">
      <div className="space-y-3">
        {steps.map((step, idx) => {
          const Icon = STATUS_ICONS[step.status] ?? Circle;
          const color = STATUS_COLORS[step.status] ?? STATUS_COLORS.pending;
          const isLast = idx === steps.length - 1;

          return (
            <div key={step.id} className="flex gap-3">
              {/* Timeline connector */}
              <div className="flex flex-col items-center">
                <div className={`mt-0.5 shrink-0 ${color}`}>
                  <Icon
                    className={`h-4 w-4 ${step.status === "active" ? "animate-spin" : ""}`}
                  />
                </div>
                {!isLast && (
                  <div className="mt-1 w-px flex-1 bg-border" />
                )}
              </div>

              {/* Content */}
              <div className="min-w-0 flex-1 pb-2">
                <p className="text-sm font-medium">
                  {step.title}
                  {step.status === "active" && isStreaming && (
                    <span className="ml-1 inline-block h-3 w-1 animate-cursor-blink bg-foreground align-middle" />
                  )}
                </p>

                {step.content && (
                  <div className="mt-1 text-sm text-muted-foreground">
                    <MessageResponse>{step.content}</MessageResponse>
                  </div>
                )}

                {step.searchResults && (
                  <div className="mt-2 rounded border border-border bg-muted/50 p-2">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Search className="h-3 w-3" />
                      <span className="font-medium">
                        Searched: &ldquo;{step.searchResults.query}&rdquo;
                      </span>
                    </div>
                    {step.searchResults.results.length > 0 && (
                      <ul className="mt-1 space-y-0.5">
                        {step.searchResults.results.map((r, i) => (
                          <li key={i} className="text-xs text-muted-foreground truncate">
                            • {r}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {step.images && step.images.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {step.images.map((img) => (
                      <figure key={img.url} className="overflow-hidden rounded-md">
                        <img
                          src={img.url}
                          alt={img.caption || ""}
                          className="h-24 w-auto object-cover"
                          loading="lazy"
                        />
                        {img.caption && (
                          <figcaption className="mt-0.5 text-[10px] text-muted-foreground">
                            {img.caption}
                          </figcaption>
                        )}
                      </figure>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
